import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProspectQuoteBuilder from '../../../ProspectQuoteBuilder';
import { apiFetch } from '../../../utils/api';

function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [prospect, setProspect] = useState(null);
  const [editedProspect, setEditedProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [converting, setConverting] = useState(false);
  
  // Quote builder state
  const [linkedQuotes, setLinkedQuotes] = useState([]);
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [labour, setLabour] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('products');
  const [savingQuote, setSavingQuote] = useState(false);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    fetchProspect();
    fetchLinkedQuotes();
    fetchProducts();
    fetchLabour();
    fetchConsumables();
    fetchNotes();
  }, [id]);

  const fetchProspect = async () => {
    try {
      const res = await apiFetch(`/api/prospects/${id}`);
      const data = await res.json();
      setProspect(data);
      setEditedProspect(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prospect:', err);
      setLoading(false);
    }
  };

  const fetchLinkedQuotes = async () => {
    try {
      const res = await apiFetch(`/api/prospect-quotes/prospect/${id}`);
      if (!res.ok) {
        setLinkedQuotes([]);
        return;
      }
      const data = await res.json();
      setLinkedQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching prospect quotes:', err);
      setLinkedQuotes([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await apiFetch('/api/products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchLabour = async () => {
    try {
      const res = await apiFetch('/api/labour');
      const data = await res.json();
      setLabour(data);
    } catch (err) {
      console.error('Error fetching labour:', err);
    }
  };

  const fetchConsumables = async () => {
    try {
      const res = await apiFetch('/api/consumables');
      const data = await res.json();
      setConsumables(data);
    } catch (err) {
      console.error('Error fetching consumables:', err);
    }
  };

  const fetchNotes = async () => {
    try {
      const res = await apiFetch(`/api/prospect-notes/${id}`);
      const data = await res.json();
      setNotes(data);
    } catch (err) {
      console.error('Error fetching notes:', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      alert('Please enter a note');
      return;
    }

    setSavingNote(true);
    try {
      const res = await apiFetch('/api/prospect-notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prospect_id: id,
          note_text: newNoteText
        })
      });

      if (res.ok) {
        setNewNoteText('');
        setShowAddNote(false);
        await fetchNotes();
      } else {
        alert('Failed to add note');
      }
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Error adding note');
    }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      const res = await apiFetch(`/api/prospect-notes/${noteId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await fetchNotes();
      } else {
        alert('Failed to delete note');
      }
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Error deleting note');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/prospects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedProspect)
      });
      
      if (res.ok) {
        setProspect(editedProspect);
        alert('Prospect updated successfully!');
      } else {
        alert('Failed to update prospect');
      }
    } catch (err) {
      console.error('Error saving prospect:', err);
      alert('Error saving prospect');
    }
    setSaving(false);
  };

  const handleConvert = async () => {
    if (!window.confirm(
      `Convert "${prospect.prospect_name}" to a customer and job?\n\nThis will create a new Customer, Site, and Job.`
    )) {
      return;
    }

    setConverting(true);
    try {
      const res = await apiFetch(`/api/prospects/${id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await res.json();
      
      if (result.success) {
        alert(`Prospect converted!\n\nJob: ${result.job_number}`);
        fetchProspect();
        if (window.confirm('Go to the new job?')) {
          navigate(`/jobs/${result.job_id}`);
        }
      } else {
        alert('Failed to convert: ' + result.error);
      }
    } catch (err) {
      console.error('Error converting:', err);
      alert('Error converting prospect');
    }
    setConverting(false);
  };

  const handleCreateQuote = () => {
    setCurrentQuote({
      customer_name: prospect.prospect_name,
      contact_name: prospect.contact_name,
      phone: prospect.phone,
      email: prospect.email,
      address: prospect.address,
      work_location: prospect.address || '',
      description: prospect.description || '',
      quote_date: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      status: 'Draft',
      terms: 'Payment due within 30 days.\nAll prices exclude VAT unless stated.\nQuote valid for 30 days from date of issue.',
      notes: `Door Location: ${prospect.door_location || 'Not specified'}`
    });
    setQuoteItems([]);
    setShowQuoteBuilder(true);
  };

  const handleAddItem = (item) => {
    setQuoteItems([...quoteItems, item]);
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...quoteItems];
    newItems[index][field] = value;
    setQuoteItems(newItems);
  };

  const handleRemoveItem = (index) => {
    setQuoteItems(quoteItems.filter((_, i) => i !== index));
  };

  const handleSaveQuote = async (total) => {
    setSavingQuote(true);
    try {
      const quoteData = {
        prospect_id: parseInt(id),
        work_location: currentQuote.work_location,
        description: currentQuote.description,
        valid_until: currentQuote.valid_until,
        status: currentQuote.status,
        terms: currentQuote.terms,
        items: quoteItems
      };

      const res = await apiFetch('/api/prospect-quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (res.ok) {
        const newQuote = await res.json();
        alert(`Quote created!\n\nRef: ${newQuote.quote_ref}\nTotal: £${total.toFixed(2)}`);
        setShowQuoteBuilder(false);
        setQuoteItems([]);
        await fetchLinkedQuotes();
      } else {
        alert('Failed to create quote');
      }
    } catch (err) {
      console.error('Error saving quote:', err);
      alert('Error saving quote');
    }
    setSavingQuote(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (!prospect) return <div className="p-8">Prospect not found</div>;

  const tabs = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'notes', label: 'Notes & Activity', count: null },
    { id: 'quote', label: 'Quotes', count: linkedQuotes.length || null }
  ];

  const canConvert = prospect.status === 'Accepted' && !prospect.converted_to_job_id;
  const isConverted = !!prospect.converted_to_job_id;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/prospects')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{prospect.prospect_name}</h1>
              <span className="text-sm text-blue-100">{prospect.status || 'New'}</span>
            </div>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
            >
              {saving ? '⏳ Saving...' : '💾 Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full px-4 py-3">
        <div className="bg-gray-50 rounded-xl shadow-sm p-2 border border-gray-200 inline-flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-8 pb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Prospect Information</h2>
                <div className="flex items-center space-x-2">
                  {canConvert && (
                    <button
                      onClick={handleConvert}
                      disabled={converting}
                      className="px-4 py-2 bg-gray-300 text-black rounded-lg font-medium hover:bg-gray-400 transition disabled:opacity-50"
                    >
                      {converting ? 'Converting...' : '✓ Convert to Job'}
                    </button>
                  )}
                  {isConverted && (
                    <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold">
                      ✓ Converted to Job
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Company/Prospect Name</label>
                    <input
                      type="text"
                      value={editedProspect.prospect_name || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, prospect_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value text-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  <div>
                    <label className="field-label mb-2 block">Contact Name</label>
                    <input
                      type="text"
                      value={editedProspect.contact_name || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, contact_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {prospect.converted_to_customer_id && (
                    <div>
                      <label className="field-label mb-2 block">Customer Account</label>
                      <div
                        onClick={() => navigate(`/customers/${prospect.converted_to_customer_id}`)}
                        className="w-full bg-green-50 border-2 border-green-300 rounded px-3 py-2 data-value text-lg cursor-pointer hover:bg-green-100 transition flex items-center justify-between"
                      >
                        <span className="text-green-700 font-semibold">Customer Created</span>
                        <span className="text-green-600 text-sm">👤 View Customer →</span>
                      </div>
                    </div>
                  )}

                  {prospect.converted_to_job_id && (
                    <div>
                      <label className="field-label mb-2 block">Job Created</label>
                      <div
                        onClick={() => navigate(`/jobs/${prospect.converted_to_job_id}`)}
                        className="w-full bg-blue-50 border-2 border-blue-300 rounded px-3 py-2 data-value text-lg cursor-pointer hover:bg-blue-100 transition flex items-center justify-between"
                      >
                        <span className="text-blue-700 font-semibold">Job Created</span>
                        <span className="text-blue-600 text-sm">📋 View Job →</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="field-label mb-2 block">Phone</label>
                    <input
                      type="text"
                      value={editedProspect.phone || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, phone: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Email</label>
                    <input
                      type="email"
                      value={editedProspect.email || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, email: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Address</label>
                    <textarea
                      value={editedProspect.address || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, address: e.target.value})}
                      rows="3"
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Door Location</label>
                    <input
                      type="text"
                      value={editedProspect.door_location || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, door_location: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                      placeholder="e.g., Main entrance, Side door, Loading bay"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Lead Source</label>
                    <input
                      type="text"
                      value={editedProspect.lead_source || ''}
                      onChange={(e) => setEditedProspect({...editedProspect, lead_source: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                      placeholder="e.g., Website, Referral, Cold call"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="field-label mb-2 block">Quote Date</label>
                      <input
                        type="date"
                        value={editedProspect.quote_date || ''}
                        onChange={(e) => setEditedProspect({...editedProspect, quote_date: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="field-label mb-2 block">Status</label>
                      <select
                        value={editedProspect.status || 'New'}
                        onChange={(e) => setEditedProspect({...editedProspect, status: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-teal-500"
                      >
                        <option>New</option>
                        <option>Quoted</option>
                        <option>Following Up</option>
                        <option>Accepted</option>
                        <option>Declined</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quote Tab */}
          {activeTab === 'quote' && (
            <div className="p-8 pb-12">
              {!showQuoteBuilder ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Quotes</h2>
                    <button
                      onClick={handleCreateQuote}
                      className="px-4 py-2 bg-gray-300 text-black rounded-lg font-medium hover:bg-gray-400 transition"
                    >
                      + Create Quote
                    </button>
                  </div>

                  {linkedQuotes.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                      <div className="mb-4 text-5xl">📋</div>
                      <p className="text-lg font-medium mb-2">No quotes yet</p>
                      <p className="text-sm">Create a detailed quote with products, labour, and consumables</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {linkedQuotes.map(quote => (
                        <div key={quote.id} className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:border-teal-400 transition shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-lg font-bold text-gray-900">{quote.quote_ref}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {formatDate(quote.quote_date)} • Status: {quote.status}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-blue-700">{formatCurrency(quote.total)}</div>
                              <button
                                onClick={() => navigate(`/quotes/${quote.id}`)}
                                className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-semibold"
                              >
                                View Quote →
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <ProspectQuoteBuilder
                  prospect={prospect}
                  quoteItems={quoteItems}
                  products={products}
                  labour={labour}
                  consumables={consumables}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  searchCategory={searchCategory}
                  setSearchCategory={setSearchCategory}
                  handleAddItem={handleAddItem}
                  handleUpdateItem={handleUpdateItem}
                  handleRemoveItem={handleRemoveItem}
                  currentQuote={currentQuote}
                  setCurrentQuote={setCurrentQuote}
                  onSave={handleSaveQuote}
                  onCancel={() => setShowQuoteBuilder(false)}
                  saving={savingQuote}
                />
              )}
            </div>
          )}

          {/* Notes & Activity Tab */}
          {activeTab === 'notes' && (
            <div className="p-8 pb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Notes & Activity</h2>
                <button
                  onClick={() => setShowAddNote(true)}
                  className="px-4 py-2 bg-gray-300 text-black rounded-lg font-medium hover:bg-gray-400"
                >
                  + Add Note
                </button>
              </div>

              {/* Add Note Form */}
              {showAddNote && (
                <div className="mb-6 p-6 bg-white rounded-lg border-2 border-teal-500 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-3">New Note</h3>
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 mb-3"
                    placeholder="Enter your note..."
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowAddNote(false);
                        setNewNoteText('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddNote}
                      disabled={savingNote}
                      className="px-6 py-2 bg-gray-300 text-black rounded-lg font-semibold hover:bg-gray-400 disabled:opacity-50"
                    >
                      {savingNote ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </div>
              )}

              {/* Notes List */}
              <div className="space-y-4 mb-8">
                {notes.length > 0 ? (
                  notes.map(note => (
                    <div key={note.id} className="bg-white border-2 border-gray-300 rounded-lg p-5 shadow-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600">
                            {new Date(note.created_at).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                      <p className="text-gray-900 whitespace-pre-wrap">{note.note_text}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-lg border-2 border-gray-300">
                    <p>No notes yet. Click "Add Note" to create one.</p>
                  </div>
                )}
              </div>

              {/* Activity History */}
              <div className="pt-6 border-t-2 border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity History</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-1.5"></div>
                    <div>
                      <p className="font-medium text-gray-900">Prospect created</p>
                      <p className="text-sm text-gray-600">{formatDate(prospect.date_created)}</p>
                    </div>
                  </div>
                  {prospect.last_contact && (
                    <div className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mt-1.5"></div>
                      <div>
                        <p className="font-medium text-gray-900">Last contact</p>
                        <p className="text-sm text-gray-600">{formatDate(prospect.last_contact)}</p>
                      </div>
                    </div>
                  )}
                  {isConverted && (
                    <div className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mt-1.5"></div>
                      <div>
                        <p className="font-medium text-gray-900">Converted to job</p>
                        <p className="text-sm text-gray-600">{formatDate(prospect.updated_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ProspectDetail;
